import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  TextField,
  Typography,
  CircularProgress
} from '@mui/material';
import { adminApi } from 'src/utils/adminApi';
import { extractPaginatedList } from 'src/utils/clutchApi';
import SurveyResponseCard from './SurveyResponseCard';

const WELLNESS_PAGE_SIZE = 6;

export default function PlayerWellnessDialog({ open, onClose, player }) {
  const userId = player?.id;
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ lastPage: 1, total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !userId) {
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError('');

    adminApi
      .userWellness(userId, {
        page,
        per_page: WELLNESS_PAGE_SIZE,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined
      })
      .then((result) => {
        if (cancelled) {
          return;
        }
        const { items, pagination: pageInfo } = extractPaginatedList(result);
        setRows(items);
        setPagination(pageInfo);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, userId, page, dateFrom, dateTo]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ color: '#F3F4F6' }}>
        Morning wellness — {player?.name}
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: '#0B0E14' }}>
        <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="From"
              InputLabelProps={{ shrink: true }}
              value={dateFrom}
              onChange={(e) => {
                setPage(1);
                setDateFrom(e.target.value);
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="To"
              InputLabelProps={{ shrink: true }}
              value={dateTo}
              onChange={(e) => {
                setPage(1);
                setDateTo(e.target.value);
              }}
            />
          </Grid>
        </Grid>

        <Box
          sx={{
            maxHeight: { xs: '52vh', md: '56vh' },
            minHeight: 120,
            overflowY: 'auto',
            pr: 0.5
          }}
        >
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {error && (
            <Typography sx={{ color: '#F87171', mb: 1, fontSize: '0.85rem' }}>
              {error}
            </Typography>
          )}

          {!loading && rows.length === 0 && (
            <Typography align="center" sx={{ color: '#9CA3AF', py: 4, fontSize: '0.85rem' }}>
              No wellness submissions in this range.
            </Typography>
          )}

          {!loading && rows.length > 0 && (
            <Stack spacing={1.25}>
              {rows.map((response) => (
                <SurveyResponseCard key={response.id} response={response} headingPrefix="Morning wellness" />
              ))}
            </Stack>
          )}
        </Box>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={1}
          sx={{ mt: 1.5, pt: 1, borderTop: '1px solid #232935' }}
        >
          <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
            Up to {WELLNESS_PAGE_SIZE} per page · Total {pagination.total || 0}
          </Typography>
          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
            <Button size="small" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>
              Previous
            </Button>
            <Typography variant="caption" sx={{ alignSelf: 'center', color: '#9CA3AF', px: 0.5 }}>
              {page} / {pagination.lastPage || 1}
            </Typography>
            <Button
              size="small"
              disabled={page >= (pagination.lastPage || 1) || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
